import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Slot } from 'radix-ui'

/*
 * The VentaHub Button (design board 06): seven variants, two sizes, radius
 * 10, 600 weight, sentence case. Never pill-shaped, never uppercase.
 *
 * - default     the one primary action a view exists for. One per view.
 * - secondary   a strong alternative in a toolbar that already has a primary.
 * - outline     the default for everything else.
 * - ghost       low-emphasis actions in dense rows.
 * - destructive the DS's "danger": always outlined red with a word, never a
 *               solid red fill — a solid red button reads as the brand primary.
 * - success     only for an explicit approve or confirm-receipt step.
 * - link        navigation inside text, never for saving data.
 *
 * Disabled is a fill, not an opacity: surface-muted with disabled text, so it
 * stays legible and never looks like a faded primary.
 */
const ICON_SIZES = ['icon', 'icon-xs', 'icon-sm', 'icon-lg'] as const

// The variants above already are VentaHub's. The `vh-*` names the AI Listing
// pages were built with are aliases of the same styles, kept only because a
// variant is never removed from `ui/` (CLAUDE.md §5.3): nothing uses them any
// more — use `default`, `outline`, `ghost`, `link` and the plain sizes.
// @deprecated vh-primary, vh-outline, vh-ghost, vh-link, vh, vh-sm, vh-icon, vh-icon-sm
const PRIMARY = 'bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active'
const OUTLINE =
  'border-border-strong bg-surface text-foreground hover:border-border-control hover:bg-surface-hover active:border-border-control active:bg-surface-muted disabled:border-border disabled:bg-surface'
const GHOST =
  'bg-transparent text-foreground-secondary hover:bg-surface-muted hover:text-foreground active:bg-border active:text-foreground disabled:bg-transparent'
const LINK =
  'bg-transparent text-primary-text underline-offset-3 hover:underline active:text-primary-active active:underline disabled:bg-transparent'

const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md border border-transparent text-sm leading-5 font-semibold whitespace-nowrap transition-[background-color,border-color,box-shadow,color] duration-100 disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-transparent disabled:bg-surface-muted disabled:text-foreground-disabled disabled:inset-shadow-none aria-busy:cursor-progress [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: PRIMARY,
        destructive:
          'border-error bg-surface text-error-foreground hover:bg-error-subtle active:bg-error-subtle active:inset-shadow-overlay-hover disabled:border-border disabled:bg-surface',
        outline: OUTLINE,
        secondary:
          'bg-secondary text-secondary-foreground hover:inset-shadow-overlay-hover active:inset-shadow-overlay-active',
        ghost: GHOST,
        link: LINK,
        success:
          'bg-success-emphasis text-success-emphasis-foreground hover:inset-shadow-overlay-hover active:inset-shadow-overlay-active',
        'vh-primary': PRIMARY,
        'vh-outline': OUTLINE,
        'vh-ghost': GHOST,
        'vh-link': cn(LINK, 'h-auto min-h-5 px-0.5'),
      },
      size: {
        default: 'h-9 px-4',
        xs: 'h-6 gap-1 px-2 text-xs',
        sm: 'h-8 gap-1.5 px-3 text-cell',
        lg: 'h-10 px-6',
        // IconButton (board 06): square, 36 or 32. Every one needs an
        // aria-label, which also becomes its tooltip.
        icon: 'size-9',
        'icon-xs': 'size-6 rounded-sm',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
        vh: 'h-9 px-4',
        'vh-sm': 'h-8 gap-1.5 px-3 text-cell',
        'vh-icon': 'size-9',
        'vh-icon-sm': 'size-8',
      },
    },
    compoundVariants: [
      // A link is text, not a box: no height or padding of its own.
      { variant: 'link', className: 'h-auto min-h-5 px-0.5' },
      // An icon-only ghost or outline button rests muted and darkens on hover.
      { variant: 'ghost', size: [...ICON_SIZES], className: 'text-muted-foreground' },
      {
        variant: 'outline',
        size: [...ICON_SIZES],
        className: 'text-foreground-secondary hover:text-foreground',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : 'button'

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
